import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { Team } from '~/core/types';

import { StatDivider } from '../../styles';
import { GoalieComparison as GoalieComparisonProp, GoalieLeader, LandingGame } from '../../types';
import { ContextSubHeader, HeaderDetails } from '../playersToWatch/components/header/CompareHeader';
import { HeaderAndSeparator } from '../playersToWatch/components/header/styles';

import GoaltenderStats from './components/GoaltenderStats/GoaltenderStats';
import TeamStats from './components/TeamStats/TeamStats';

import { GoalieComparisonContainer, TeamGoaltenderStatContainer } from './styles';

type Props = {
    game: LandingGame;
};

type GoalieAndTeamDisplayProps = {
    game: LandingGame;
    goalieComparison?: GoalieComparisonProp;
    team: Team;
};

const GoalieAndTeamDisplay: FC<GoalieAndTeamDisplayProps> = ({ game, goalieComparison, team }) => {
    // We only want to show two goaltenders for a team, we'll pick based on the most games played
    const leaders = goalieComparison?.leaders
        ? [...goalieComparison.leaders]
              .sort((g1: GoalieLeader, g2: GoalieLeader) => (g2?.gamesPlayed || 0) - (g1?.gamesPlayed || 0))
              .slice(0, 2)
        : [];

    return leaders.length > 0 || goalieComparison?.teamTotals?.record ? (
        <TeamGoaltenderStatContainer>
            <TeamStats gameType={game.gameType} team={team} teamTotals={goalieComparison?.teamTotals} />
            {leaders.map((leader) => (
                <GoaltenderStats key={leader.playerId} gameType={game.gameType} goalie={leader} team={team} />
            ))}
        </TeamGoaltenderStatContainer>
    ) : null;
};

const GoalieComparison: FC<Props> = ({ game }) => {
    const screenSize = useSizeWithThrottle();

    const isLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;
    const goalieComparison = game?.matchup?.goalieComparison;

    if (!goalieComparison) {
        return null;
    }

    const goalieComparisonMessage = (
        <FormattedMessage
            description="A word describing a team and the teams goaltenders current stats"
            defaultMessage="Goaltending"
        />
    );
    return (
        <GoalieComparisonContainer>
            <HeaderAndSeparator>
                <HeaderDetails message={goalieComparisonMessage}>
                    <ContextSubHeader contextDescriptor={game?.matchup?.goalieComparison} currentSeason={game.season} />
                </HeaderDetails>
                {isLarge && <StatDivider />}
            </HeaderAndSeparator>
            <GoalieAndTeamDisplay game={game} goalieComparison={goalieComparison.awayTeam} team={game.awayTeam} />
            <GoalieAndTeamDisplay game={game} goalieComparison={goalieComparison.homeTeam} team={game.homeTeam} />
        </GoalieComparisonContainer>
    );
};

export default GoalieComparison;
